import { createContext, useCallback, useContext, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import {
  exploreCounts,
  exploreRecords,
  type ExploreCell,
  type ExploreFact,
  type ExploreFormat,
  type ExploreMeasure,
  type ExploreRef,
  type ExploreResult,
  type ExploreRow
} from '../lib/adminClient'
import { dateTime, num, usdFine } from '../lib/format'
import { DetailGrid, ExpandableRow, useOpenRow, type Fact } from './RowDetail'

// Every figure in the console opens the records it was counted from. Owner,
// 2026-10-30: "make all drill downs, infinite to the source of the data
// including the individual records they are made up of".
//
// A figure is wrapped in <Drillable> (a tile, a bar, a chart point) or
// <DrillValue> (a number in a table). Clicking it opens this panel on the
// set of records behind it (GET /admin/explore, src/adminExplore.ts on the
// server). Each record opens to its full detail, links to the records it
// points at, and lists the sets related to it with their counts; any of those
// opens as the next level, with a trail back. There is no bottom: every
// record leads on until the data does not.
//
// When the figure passes the number it showed (`expect`), the panel checks the
// records against it and says plainly whether they add up. A mismatch is shown,
// never smoothed over: it means the figure and its records disagree.

export interface DrillOrigin {
  /** What was clicked, in words: "Signups on 2026-10-29". */
  label: string
  /** The number the figure showed, and which of the records' sums it should equal. */
  expect?: { key: string; value: number }
}

interface Level {
  ref: ExploreRef
  origin?: DrillOrigin
}

const DrillContext = createContext<{ open: (ref: ExploreRef, origin?: DrillOrigin) => void } | null>(null)

export function useDrill(): { open: (ref: ExploreRef, origin?: DrillOrigin) => void } {
  const ctx = useContext(DrillContext)
  if (!ctx) throw new Error('useDrill needs a DrillProvider above it (components/Layout.tsx)')
  return ctx
}

export function DrillProvider({ children }: { children: ReactNode }): JSX.Element {
  const [stack, setStack] = useState<Level[]>([])
  const open = useCallback((ref: ExploreRef, origin?: DrillOrigin) => setStack([{ ref, origin }]), [])
  return (
    <DrillContext.Provider value={{ open }}>
      {children}
      {stack.length > 0 && <DrillPanel stack={stack} setStack={setStack} />}
    </DrillContext.Provider>
  )
}

const INTERACTIVE = 'button, a, input, select, textarea, label, summary, [role="switch"], [data-no-row-toggle]'

/** A tile, bar or any block figure that opens its records. */
export function Drillable({
  to,
  label,
  expect,
  children,
  className = '',
  testId
}: {
  to: ExploreRef
  label: string
  expect?: { key: string; value: number }
  children: ReactNode
  className?: string
  testId?: string
}): JSX.Element {
  const drill = useDrill()
  const go = (): void => drill.open(to, { label, expect })
  const onClick = (e: MouseEvent<HTMLDivElement>): void => {
    const control = (e.target as Element).closest(INTERACTIVE)
    if (control && e.currentTarget.contains(control)) return
    go()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
    e.preventDefault()
    go()
  }
  return (
    <div
      role="button"
      tabIndex={0}
      className={`drillable ${className}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      title={`See the records behind ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
      data-testid={testId}
    >
      {children}
    </div>
  )
}

/** A number or name inside a table or sentence that opens its records. */
export function DrillValue({
  to,
  label,
  expect,
  children,
  testId
}: {
  to: ExploreRef
  label: string
  expect?: { key: string; value: number }
  children: ReactNode
  testId?: string
}): JSX.Element {
  const drill = useDrill()
  return (
    <button
      type="button"
      className="drill-link"
      onClick={(e) => {
        e.stopPropagation()
        drill.open(to, { label, expect })
      }}
      title={`See the records behind ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
      data-testid={testId}
    >
      {children}
    </button>
  )
}

// ── formatting ──────────────────────────────────────────────────────────

function fmtText(v: ExploreCell, f?: ExploreFormat): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  switch (f) {
    case 'int':
      return num(Number(v))
    case 'usd':
      return usdFine(Number(v))
    case 'credits':
      return `${(Math.round(Number(v) * 100) / 100).toLocaleString('en-US')} credits`
    case 'datetime':
      return dateTime(Number(v))
    default:
      return String(v)
  }
}

function fmtNode(v: ExploreCell, f?: ExploreFormat): ReactNode {
  if (v === null || v === undefined || v === '') return null
  const text = fmtText(v, f)
  return f === 'mono' ? <span className="font-mono break-all">{text}</span> : text
}

const TOLERANCE: Partial<Record<ExploreFormat, number>> = { usd: 0.005, credits: 0.01 }
function adds(m: ExploreMeasure, expected: number): boolean {
  return Math.abs(m.value - expected) <= (TOLERANCE[m.format] ?? 0)
}

const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

function csvOf(res: ExploreResult, rows: ExploreRow[]): string {
  const q = (s: string): string => `"${s.replace(/"/g, '""')}"`
  const head = res.columns.map((c) => q(c.label)).join(',')
  const body = rows.map((r) =>
    res.columns
      .map((c) => {
        const v = r.cells[c.key]
        // Unknown stays empty, never 0.
        if (v === null || v === undefined || v === '') return '""'
        if (c.format === 'datetime') return q(new Date(Number(v)).toISOString())
        return q(String(v))
      })
      .join(',')
  )
  return [head, ...body].join('\n')
}

// ── the panel ───────────────────────────────────────────────────────────

function DrillPanel({ stack, setStack }: { stack: Level[]; setStack: (f: (s: Level[]) => Level[]) => void }): JSX.Element {
  const [titles, setTitles] = useState<Record<number, string>>({})
  const panelRef = useRef<HTMLElement>(null)
  const depth = stack.length - 1
  const level = stack[depth]
  const close = (): void => setStack(() => [])
  const push = (ref: ExploreRef, origin?: DrillOrigin): void => setStack((s) => [...s, { ref, origin }])
  const jump = (i: number): void => setStack((s) => s.slice(0, i + 1))

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') setStack(() => [])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setStack])
  useEffect(() => {
    panelRef.current?.focus()
    panelRef.current?.scrollTo({ top: 0 })
  }, [depth])

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Records">
      <div className="absolute inset-0 bg-black/55" onClick={close} data-testid="drill-backdrop" />
      <section
        ref={panelRef}
        tabIndex={-1}
        className="drill-panel relative h-full w-full max-w-[920px] overflow-y-auto outline-none"
        data-testid="drill-panel"
      >
        <header className="sticky top-0 z-10 drill-panel-head px-6 py-3 flex items-center gap-3">
          <nav className="flex-1 min-w-0 flex flex-wrap items-center gap-1 text-xs" aria-label="Trail" data-testid="drill-trail">
            {stack.map((l, i) => (
              <span key={i} className="flex items-center gap-1 min-w-0">
                {i > 0 && <span className="text-slate-600">›</span>}
                {i === depth ? (
                  <span className="text-slate-200 truncate" aria-current="page">{titles[i] ?? l.origin?.label ?? '…'}</span>
                ) : (
                  <button type="button" className="text-slate-400 hover:text-white truncate" onClick={() => jump(i)}>
                    {titles[i] ?? l.origin?.label ?? '…'}
                  </button>
                )}
              </span>
            ))}
          </nav>
          {depth > 0 && (
            <button type="button" className="btn-ghost text-xs" onClick={() => jump(depth - 1)} data-testid="drill-back">
              ← Back
            </button>
          )}
          <button type="button" className="btn-ghost text-xs" onClick={close} aria-label="Close records" data-testid="drill-close">
            ✕
          </button>
        </header>
        <DrillLevel
          key={`${depth}:${JSON.stringify(level.ref)}`}
          level={level}
          onOpen={push}
          onTitle={(t) => setTitles((m) => (m[depth] === t ? m : { ...m, [depth]: t }))}
        />
      </section>
    </div>
  )
}

const PAGE = 100

function DrillLevel({ level, onOpen, onTitle }: { level: Level; onOpen: (ref: ExploreRef, origin?: DrillOrigin) => void; onTitle: (t: string) => void }): JSX.Element {
  const [res, setRes] = useState<ExploreResult | null>(null)
  const [rows, setRows] = useState<ExploreRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [more, setMore] = useState(false)
  const [csvBusy, setCsvBusy] = useState(false)
  const openRow = useOpenRow()
  const autoOpened = useRef(false)

  useEffect(() => {
    let cancelled = false
    void exploreRecords(level.ref, { limit: PAGE }).then((r) => {
      if (cancelled) return
      if (!r.ok) {
        setError(r.error)
        return
      }
      setRes(r.data)
      setRows(r.data.rows)
    })
    return () => {
      cancelled = true
    }
  }, [level.ref])

  useEffect(() => {
    if (!res) return
    onTitle(level.origin?.label ?? `${res.title}${res.filters.length ? ` · ${res.filters[0]}` : ''}`)
    // One record: show it, rather than a list of one to click.
    if (res.total === 1 && rows[0] && !autoOpened.current) {
      autoOpened.current = true
      openRow.toggle(rows[0].id)
    }
  }, [res, rows, level.origin, onTitle, openRow])

  async function loadMore(): Promise<void> {
    if (!res) return
    setMore(true)
    const r = await exploreRecords(level.ref, { limit: PAGE, offset: rows.length })
    setMore(false)
    if (r.ok) setRows((prev) => [...prev, ...r.data.rows])
    else setError(r.error)
  }

  async function downloadCsv(): Promise<void> {
    if (!res) return
    setCsvBusy(true)
    const all: ExploreRow[] = []
    // Every matching record, a page at a time, capped well above any real set.
    while (all.length < Math.min(res.total, 50_000)) {
      const r = await exploreRecords(level.ref, { limit: 1000, offset: all.length })
      if (!r.ok) {
        setError(r.error)
        setCsvBusy(false)
        return
      }
      if (r.data.rows.length === 0) break
      all.push(...r.data.rows)
    }
    setCsvBusy(false)
    const url = URL.createObjectURL(new Blob([csvOf(res, all)], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${res.set}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (error) {
    return (
      <div className="px-6 py-6">
        <div className="glass-card rounded-xl p-4 text-sm text-red-300" data-testid="drill-error">
          Could not load these records: {error}
        </div>
      </div>
    )
  }
  if (!res) return <div className="px-6 py-6 text-sm text-slate-400" data-testid="drill-loading">Loading records…</div>

  const expected = level.origin?.expect
  const checked = expected ? res.measures.find((m) => m.key === expected.key) : undefined

  return (
    <div className="px-6 py-5 grid gap-4" data-testid="drill-level" data-set={res.set}>
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Records</div>
        <h2 className="text-xl font-semibold mt-1" data-testid="drill-title">{res.title}</h2>
        {level.origin && <div className="text-sm text-slate-300 mt-1">Behind: {level.origin.label}</div>}
        <p className="text-xs text-slate-400 mt-2 max-w-[70ch]" data-testid="drill-basis">{res.basis}</p>
        {res.filters.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="drill-filters">
            {res.filters.map((f) => (
              <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{f}</span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="drill-measures">
        {res.measures.map((m) => (
          <div key={m.key} className="glass-card rounded-lg px-3 py-2" data-testid={`drill-measure-${m.key}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400">{m.label}</div>
            <div className="text-base font-semibold tabular-nums">{fmtText(m.value, m.format)}</div>
          </div>
        ))}
      </div>

      {expected && checked && (
        adds(checked, expected.value) ? (
          <div className="text-sm text-emerald-300" data-testid="drill-match">
            ✓ These records add up to {fmtText(checked.value, checked.format)}, the figure you opened.
          </div>
        ) : (
          <div className="glass-card rounded-xl p-3 text-sm text-amber-200 border border-amber-400/30" data-testid="drill-mismatch">
            These records add up to {fmtText(checked.value, checked.format)}, but the figure showed{' '}
            {fmtText(expected.value, checked.format)}. The figure and the records it was counted from disagree; the
            records are what is stored.
          </div>
        )
      )}
      {res.note && (
        <div className="glass-card rounded-xl p-3 text-sm text-amber-200 border border-amber-400/30" data-testid="drill-note">
          {res.note}
        </div>
      )}

      {res.total === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="drill-empty">
          No records match.
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-x-auto">
          <table className="admin-table w-full" data-testid="drill-table">
            <thead>
              <tr>
                {res.columns.map((c) => (
                  <th key={c.key} className={c.format === 'int' || c.format === 'usd' || c.format === 'credits' ? 'text-right' : ''}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ExpandableRow
                  key={row.id}
                  open={openRow.isOpen(row.id)}
                  onToggle={() => openRow.toggle(row.id)}
                  colSpan={res.columns.length}
                  testId={`drill-row-${row.id}`}
                  detail={() => <RecordDetail row={row} onOpen={onOpen} />}
                >
                  {res.columns.map((c) => {
                    const ref = row.refs?.[c.key]
                    const v = row.cells[c.key]
                    const numeric = c.format === 'int' || c.format === 'usd' || c.format === 'credits'
                    return (
                      <td key={c.key} className={`${numeric ? 'text-right tabular-nums' : ''} ${c.format === 'mono' ? 'font-mono text-xs' : ''}`}>
                        {ref && v !== null && v !== '' ? (
                          <button type="button" className="drill-link" onClick={() => onOpen(ref, { label: `${c.label}: ${fmtText(v, c.format)}` })}>
                            {fmtText(v, c.format)}
                          </button>
                        ) : (
                          <span className={v === null || v === '' ? 'text-slate-500' : ''}>{fmtText(v, c.format)}</span>
                        )}
                      </td>
                    )
                  })}
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        <span data-testid="drill-count">
          {rows.length === res.total ? `${num(res.total)} ${res.total === 1 ? 'record' : 'records'}` : `Showing ${num(rows.length)} of ${num(res.total)}`}
        </span>
        {rows.length < res.total && (
          <button type="button" className="btn-ghost text-xs" onClick={() => void loadMore()} disabled={more} data-testid="drill-more">
            {more ? 'Loading…' : `Show ${Math.min(PAGE, res.total - rows.length)} more`}
          </button>
        )}
        {res.total > 0 && (
          <button type="button" className="btn-ghost text-xs" onClick={() => void downloadCsv()} disabled={csvBusy} data-testid="drill-csv">
            {csvBusy ? 'Preparing…' : 'Download CSV'}
          </button>
        )}
      </div>
    </div>
  )
}

function FactValue({ fact, onOpen }: { fact: ExploreFact; onOpen: (ref: ExploreRef, origin?: DrillOrigin) => void }): JSX.Element | null {
  const shown = fmtNode(fact.v, fact.format)
  if (shown === null) return null
  if (!fact.ref) return <>{shown}</>
  return (
    <button type="button" className="drill-link text-left" onClick={() => onOpen(fact.ref!, { label: `${fact.label}: ${fmtText(fact.v, fact.format)}` })}>
      {shown}
    </button>
  )
}

function RecordDetail({ row, onOpen }: { row: ExploreRow; onOpen: (ref: ExploreRef, origin?: DrillOrigin) => void }): JSX.Element {
  const facts: Fact[] = row.facts.filter((f) => !f.wide).map((f) => [f.label, <FactValue fact={f} onOpen={onOpen} />])
  const wide: Fact[] = row.facts.filter((f) => f.wide).map((f) => [f.label, <FactValue fact={f} onOpen={onOpen} />])
  return (
    <div className="grid gap-3" data-testid="drill-record">
      <DetailGrid facts={facts} wide={wide} />
      {row.related.length > 0 && <Related related={row.related} onOpen={onOpen} />}
    </div>
  )
}

function Related({ related, onOpen }: { related: ExploreRow['related']; onOpen: (ref: ExploreRef, origin?: DrillOrigin) => void }): JSX.Element {
  const [counts, setCounts] = useState<Array<number | null> | null>(null)
  useEffect(() => {
    let cancelled = false
    void exploreCounts(related.map((r) => r.ref)).then((r) => {
      if (!cancelled) setCounts(r.ok ? r.data.counts : related.map(() => null))
    })
    return () => {
      cancelled = true
    }
  }, [related])
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">Related records</div>
      <div className="flex flex-wrap gap-1.5" data-testid="drill-related">
        {related.map((r, i) => {
          const c = counts?.[i]
          return (
            <button
              key={r.label}
              type="button"
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${c === 0 ? 'border-white/5 text-slate-500 hover:text-slate-300' : 'border-accent/30 text-slate-200 hover:bg-accent/15'}`}
              onClick={() => onOpen(r.ref, { label: r.label })}
              data-testid={`drill-related-${slug(r.label)}`}
            >
              {r.label}
              <span className="ml-1.5 tabular-nums text-slate-400">{counts === null ? '…' : c === null ? '?' : num(c)}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** An account named anywhere in the console, opening its record and everything about it. */
export function AccountLink({ id, children }: { id: string | null | undefined; children: ReactNode }): JSX.Element {
  if (!id || id === 'unknown') return <>{children}</>
  return (
    <DrillValue to={{ set: 'accounts', filters: { id } }} label={`Account ${typeof children === 'string' ? children : id}`}>
      {children}
    </DrillValue>
  )
}
